import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
  Breakpoint,
} from "@mui/material";
import { Close } from "@mui/icons-material";

export interface ResponsiveDialogProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  headerAction?: React.ReactNode;
  children: React.ReactNode;
  actions?: React.ReactNode;
  maxWidth?: Breakpoint | false;
  fullWidth?: boolean;
  fullScreenOnMobile?: boolean;
  contentSx?: object;
}

export const ResponsiveDialog: React.FC<ResponsiveDialogProps> = ({
  open,
  onClose,
  title,
  subtitle,
  headerAction,
  children,
  actions,
  maxWidth = "md",
  fullWidth = true,
  fullScreenOnMobile = true,
  contentSx,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isFullScreen = fullScreenOnMobile && isMobile;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={isFullScreen}
      PaperProps={{
        sx: {
          borderRadius: isFullScreen ? 0 : { xs: 2, sm: 3 },
          m: isFullScreen ? 0 : { xs: 1, sm: 2, md: 3 },
          maxHeight: isFullScreen ? "100%" : "90vh",
          width: isFullScreen ? "100%" : { xs: "calc(100% - 16px)", sm: "auto" },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      {(title || onClose) && (
        <DialogTitle
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: { xs: 1.25, sm: 1 },
          }}
        >
          {/* Top row: Title + Close Icon (mobile) or Title alone (desktop) */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              width: "100%",
              gap: 1,
              flex: 1,
              minWidth: 0,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {typeof title === "string" ? (
                <Typography
                  variant="h5"
                  component="h2"
                  fontWeight={700}
                  sx={{
                    fontSize: { xs: "1.05rem", sm: "1.25rem" },
                    lineHeight: 1.25,
                  }}
                >
                  {title}
                </Typography>
              ) : (
                title
              )}
              {subtitle && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    mt: 0.25,
                    fontSize: { xs: "0.75rem", sm: "0.8rem" },
                    lineHeight: 1.3,
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>

            {/* Mobile Close Button */}
            <IconButton
              aria-label="Close dialog"
              onClick={onClose}
              size="small"
              sx={{
                display: { xs: "flex", sm: "none" },
                width: 32,
                height: 32,
                color: "text.secondary",
                "&:hover": { color: "text.primary", bgcolor: "action.hover" },
                flexShrink: 0,
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>

          {/* Actions row / Desktop Close */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: { xs: "100%", sm: "auto" },
              justifyContent: { xs: "stretch", sm: "flex-end" },
              flexShrink: 0,
            }}
          >
            {headerAction && (
              <Box
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  "& > *": { width: { xs: "100%", sm: "auto" } },
                }}
              >
                {headerAction}
              </Box>
            )}
            {/* Desktop Close Button */}
            <IconButton
              aria-label="Close dialog"
              onClick={onClose}
              size="small"
              sx={{
                display: { xs: "none", sm: "flex" },
                width: 36,
                height: 36,
                color: "text.secondary",
                "&:hover": { color: "text.primary", bgcolor: "action.hover" },
                flexShrink: 0,
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
      )}

      <DialogContent
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          flex: 1,
          overflowY: "auto",
          boxSizing: "border-box",
          ...contentSx,
        }}
      >
        {children}
      </DialogContent>

      {actions && (
        <DialogActions
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderTop: 1,
            borderColor: "divider",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ResponsiveDialog;
